<script setup lang="ts">
import { computed } from 'vue'
import { MapPinned } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n';
import { useAddress } from '../composables/address'
import type { Page } from '@repo/types';

const { t } = useI18n();
const { getAddressPart } = useAddress()

const props = withDefaults(defineProps<{
  page: Page;
  featured?: boolean;
}>(), {
  featured: false
});

const bgImage = computed(() => props.featured ? (props.page.data?.thumbnail?.source || props.page.data?.imageUrls?.[0]) : null)
</script>

<template>
  <div
    class="group relative rounded-xl border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300 flex flex-col"
    :class="bgImage ? 'min-h-[200px] border-none justify-end' : 'bg-white hover:border-sky-200'"
  >
    <!-- Background Image -->
    <template v-if="bgImage">
      <img
        :src="bgImage"
        class="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
      />
      <div class="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent"></div>
    </template>

    <div class="relative z-10 flex h-full">
      <!-- Main Link: Browse -->
      <a
        :href="'/' + (page.canonical ?? page.path)"
        class="flex-1 p-6 transition-colors"
        :class="bgImage ? 'text-white' : 'hover:bg-gray-50'"
      >
        <div class="flex items-center justify-between">
          <div>
            <h3
              class="font-bold text-lg transition-colors"
              :class="bgImage ? 'text-white' : 'text-gray-900 group-hover:text-sky-600'"
            >
              {{ getAddressPart(page) }}
            </h3>
            <p
              v-if="typeof page.stats?.count === 'number'"
              class="text-sm mt-1"
              :class="bgImage ? 'text-gray-200' : 'text-gray-500'"
            >
              {{ page.stats?.count?.toLocaleString() }} {{ t('searchResults.properties') }}
            </p>
          </div>
          <svg
            v-if="!bgImage"
            class="h-5 w-5 text-gray-400 group-hover:translate-x-1 transition-transform"
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </a>

      <!-- Quick Search: Search Results -->
      <a
        :href="'/' + (page.canonical ?? page.path) + '/search'"
        class="overflow-hidden flex flex-col items-center justify-center transition-all duration-300 ease-in-out"
        :class="[
          bgImage
            ? 'w-12 hover:w-20 bg-white/10 hover:bg-white/20 backdrop-blur-md border-l border-white/10 text-white'
            : 'w-0 group-hover:w-24 bg-sky-600 hover:bg-sky-800 text-white'
        ]"
        :title="t('search.map')"
      >
        <MapPinned class="h-6 w-6" />
        <span
          v-if="!bgImage"
          class="text-[10px] font-bold mt-1 uppercase tracking-wider"
        >
          {{ t('searchResults.mapView').split(' ')[0] }}
        </span>
      </a>
    </div>
  </div>
</template>
